import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HelpersService {
  private projectName = 'smart-campus-luiss-storage-';
  private storageCredentials = this.projectName + 'credentials';
  private storageIsLogged = this.projectName + 'is-logged';
  private storageUser = this.projectName + 'user';
  private storageAccessTokenKey = this.projectName + 'access-token';
  private storageRefreshTokenKey = this.projectName + 'refresh-token';
  private storageExpiresIn = this.projectName + 'expires-in';
  private storageSamlTokenKey = this.projectName + 'saml-token';

  public refreshPage = false;
  public timeoutRefresh: any;
  private refreshToken$: Subscription;
  constructor(private toastrService: ToastrService,
              private http: HttpClient,
              private router: Router) {
                this.initToastrService();
              }

  get credentials(): string {
      return localStorage.getItem(this.storageCredentials) as string;
  }

  public setCredentials(user: any): void {
    if (user) {
      const credentials = btoa(user.username + ':' + user.password);
      localStorage.setItem(this.storageCredentials, credentials);
    } else {
      localStorage.removeItem(this.storageCredentials);
    }
  }

  get user(): any {
      return JSON.parse(localStorage.getItem(this.storageUser) as string);
  }

  setUser(user: any): void {
      localStorage.setItem(this.storageUser, JSON.stringify(user));
  }

  get isLogged(): boolean {
    const isLogged = localStorage.getItem(this.storageIsLogged) as string;
    return isLogged && isLogged === '1' ? true : false;
  }

  public setIsLogged(isLogged: boolean): void {
    localStorage.setItem(this.storageIsLogged, isLogged ? '1' : '0');
  }

  public get expiresIn(): number {
      return Number(localStorage.getItem(this.storageExpiresIn));
  }

  get samlToken(): string {
      return <string>localStorage.getItem(this.storageSamlTokenKey);
  }

  get accessToken(): string {
      return <string>localStorage.getItem(this.storageAccessTokenKey);
  }

  get refreshToken(): string {
      return <string>localStorage.getItem(this.storageRefreshTokenKey);
  }

  get headers(): object {
    return  {headers: this.getRequestHeader()};
  }

  protected getRequestHeader(): HttpHeaders {
    return new HttpHeaders().set('Authorization', `Basic ${this.credentials}`);
  }

  public getHTTPHeader(isAuth = true) {
      if (!isAuth) {
          return {
              headers: new HttpHeaders({
                  'Content-Type': 'application/json',
              })
          };
      } else {
          const token: string = this.accessToken;
          return {
              headers: new HttpHeaders({
                  'Content-Type': 'application/json',
                  Authorization: 'Bearer ' + token,
              })
          };
      }
  }

  public get fileHeaders(): any {
    return {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept-language': 'en',
            'Authorization': `Basic ${this.credentials}`
        }),
        'responseType': 'arraybuffer' as 'arraybuffer' };
  }

  timeToDecimal(t: string): number {
    const arr = t.split(':');
    const hour = Number(arr[1]);
    const dec = (hour / 6) * 10;
    return parseFloat(parseInt(arr[0], 10) + '.' + (dec < 10 ? '0' : '') + dec);
  }

  showError(error: any): void {
    this.toastrService.error(this.getError(error));
  }

  public getError(error: any): string {
    let errorMsg = '';
    if (error && typeof error === 'string') {
        errorMsg = error;
    } else if (error.error &&  error.error.error && error.error.error.message) {
        errorMsg = error.error.error.message;
    } else if (!errorMsg) {
        errorMsg = 'Server momentaneamente non raggiungibile.';
    }
    return errorMsg;
  }

  public showSuccess(): void {
      this.toastrService.success('Operazione conclusa con successo!');
  }

  public downloadFile(blob: any, fileName: any): void {
    if (blob.size !== 0) {
      const file: any = {};
      file.nomeFile = fileName;
      if (navigator.msSaveOrOpenBlob) { // ie
        file.blob = blob;
        if (file.blob) {
         navigator.msSaveOrOpenBlob(file.blob, file.nomeFile);
        }
      } else { // altri browser
        file.urlFile = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = file.urlFile;
        link.download = file.nomeFile;
        link.click();
      }
    }
  }

  public date2str(date: Date): string {
      const day = date.getDate();
      const dd = day < 10 ? '0' + day : day;
      const month = date.getMonth() + 1;
      const mm = month < 10 ? '0' + month : month;
      const yyyy = date.getFullYear();
      const hours = date.getHours();
      const hh = hours < 10 ? '0' + hours : hours;
      const minutes = date.getMinutes();
      const mi = minutes < 10 ? '0' + minutes : minutes;
      return `${dd}${mm}${yyyy}`;
  }

  public logout(): void {
    if (this.accessToken) {
      this.logoutAdfs().subscribe((res) => {
        this.clear();
        window.open('https://adfs.luiss.it/adfs/ls/?wa=wsignout1.0','_blank');
        this.router.navigateByUrl('/login');
      }, err => {
        this.clear();
        window.open('https://adfs.luiss.it/adfs/ls/?wa=wsignout1.0','_blank');
        this.router.navigateByUrl('/login');
      })
    } else {
      this.clear();
      this.router.navigateByUrl('/login');
    }
  }

  public clear() {
      localStorage.removeItem(this.storageAccessTokenKey);
      localStorage.removeItem(this.storageRefreshTokenKey);
      localStorage.removeItem(this.storageUser);
      localStorage.removeItem(this.storageExpiresIn);
      localStorage.removeItem(this.storageIsLogged);
      localStorage.removeItem(this.storageCredentials);
  }

  private initToastrService(): void {
      this.toastrService.toastrConfig.closeButton = false;
      this.toastrService.toastrConfig.newestOnTop = true;
      this.toastrService.toastrConfig.progressBar = false;
      this.toastrService.toastrConfig.positionClass = 'toast-bottom-right';
      this.toastrService.toastrConfig.preventDuplicates = false;
      this.toastrService.toastrConfig.timeOut = 2000;
      this.toastrService.toastrConfig.extendedTimeOut = 2000;
  }

  public checkRole(role: string): boolean {
      return this.checkRoles([role]);
  }

  public checkRoles(roles: string[]): boolean {
      let ret = false;
      if (this.user && this.user.roles && this.user.roles.length && roles && roles.length) {
          for (const role of roles) {
              if (this.user.roles.indexOf(role) >= 0) {
                  ret = true;
                  break;
              }
          }
      }
      return ret;
  }

  public routeInit(): void {
    if (this.user) {
        this.router.navigateByUrl('/notifications');
    //   if (this.checkRole('ROLE_BACKOFFICE_TUTOR')) {
    //     this.router.navigateByUrl('/tutoring');
    //   } else if (this.checkRole('ROLE_BACKOFFICE_LESSON')) {
    //     this.router.navigateByUrl('/lectures');
    //   } else if (this.checkRole('ROLE_SUPERVISOR')) {
    //     this.router.navigateByUrl('/presences');
    //   }
    } else {
      this.router.navigateByUrl('/login');
    }
  }

  public login(saml_token: string): Observable<any> {
    let url = `${environment.urlApi}/oauth/v2/token`;
    const req = {
      client_id: environment.clientId,
      client_secret: environment.secretId,
      grant_type: "http://luiss.com/grants",
      saml_token: saml_token
    };
    return this.http.post(url, req, this.getHTTPHeader(false)).pipe(
        map((res: any) => {
            this.saveAccessToken(res, saml_token);
            this.startRefreshInterval();
            return res;
        }),
        catchError(error => {
            return this.handleError(error);
        })
    );
  }

  public logoutAdfsOLD(): Observable<any> {
    let url = `${environment.urlApi}/adfs/saml/logout`;
    const req = {};
    // const headers = {
    //     headers: new HttpHeaders({
    //         'Content-Type': 'application/json',
    //         'ADFS-Authorization': this.accessToken
    //     })
    // }
    return this.http.post(url, req, this.getHTTPHeader(true)).pipe(
        map((res: any) => {
            return res;
        }),
        catchError(error => {
            return this.handleError(error);
        })
    );
  }

  

  public logoutAdfs(): Observable<any> {
    let url = `${environment.urlApi}/ext/bo-push-notifications/logout`;
    return this.http.get(url, this.getHTTPHeader(true)).pipe(
        map((res: any) => {
            return res;
        }),
        catchError(error => {
            return this.handleError(error);
        })
    );
  }

  public saveAccessToken(res: any, saml_token: string) {
      localStorage.setItem(this.storageAccessTokenKey, res.access_token);
      localStorage.setItem(this.storageRefreshTokenKey, res.refresh_token);
      const expiresIn = String((new Date().valueOf()) + (res.expires_in * 0.95 * 1000));
      localStorage.setItem(this.storageExpiresIn, expiresIn);
      localStorage.setItem(this.storageSamlTokenKey, saml_token);
  }

  public saveUser(res: any) {
      const userStringify = JSON.stringify(res);
      localStorage.setItem(this.storageUser, userStringify);
  }

  public hasExpired(epochDate: any):boolean{
      let date = new Date();
      let todayEpoch = date.valueOf();
      if(todayEpoch < epochDate){
          return false;
      } else{
          return true;
      }

  }

  public startRefreshInterval() {
      if (this.expiresIn) {
          let interval = this.expiresIn - new Date().valueOf();
          interval = interval < 0 ? 0 : interval;            
          clearTimeout(this.timeoutRefresh);
          console.log('end interval refresh ', new Date(new Date().getTime() + interval));
          this.timeoutRefresh = setTimeout(() => {
              this.callRefreshTokenOauth();
          }, interval);
      } else {
          this.clear();
      }
  }

  public callRefreshTokenOauth() {
      if (this.refreshToken$ instanceof Subscription) {
          this.refreshToken$.unsubscribe();
      }
      this.refreshToken$ = this.refreshTokenOauth2().subscribe(res => {
          this.saveAccessToken(res, this.samlToken);
          clearTimeout(this.timeoutRefresh);
          this.startRefreshInterval();
          if (this.refreshPage) {
              location.reload();
          }
      }, err => {
          this.clear();
          this.router.navigateByUrl('/auth');
      });
  }

  private refreshTokenOauth2() {
    let url = `${environment.urlApi}/oauth/v2/token`;
    const req = {
        client_id: environment.clientId,
        client_secret: environment.secretId,
        grant_type: 'refresh_token',
        refresh_token: this.refreshToken
    };
    return this.http.post(url, req).pipe(map((res: any) => {
        return res;
    }), catchError(err => {
        return throwError(err);
    }));
  }

  public handleError(error: Response) {
      switch (error.status) {
          case 400:
              break;
          case 401:
              this.handle401(error);
              break;
          case 403:
              this.router.navigateByUrl('/unauthorized');
              break;
          case 500:
              break;
      }
      return throwError(error);
  }

  private handle401(error: any) {
      this.refreshPage = true;
      this.callRefreshTokenOauth();
  }

}
